import { FC } from 'react';
import {
  DEFAULT_PAGINATION_PAGE_COUNT,
  SONNER_ERROR_OPTIONS,
  SONNER_SUCCESS_OPTIONS,
} from '@/common/constants';
import { useTablePagination } from '@/hooks/useTablePagination';
import { Outlet, useNavigate } from 'react-router-dom';
import { AlertDialogWrapper } from '@/components/shared/AlertDialog';
import { useMutation } from '@tanstack/react-query';
import SuccessToastText from '@/components/shared/SuccessToastText';
import { AxiosError } from 'axios';
import { toast } from 'sonner';
import { TableWrapper } from '@/components/shared/TableWrapper';
import * as Sentry from '@sentry/react';
import {
  deleteDonationMutationFn,
  toggleEnabledMutationFn,
  useDonations,
} from '@/api/donation/donation.api';
import { DonationTableColumns } from './utils/DonationTableColumns';
import { DonationApi } from '@/api/donation/donation.model';
import { pathsBase } from '@/routes/paths';
import { SearchBar } from '@/components/shared/SearchBar';
import { useSearchBar } from '@/hooks/useSearchBar';
import { useAlertDialog } from '@/hooks/useAlertDialog';
import useFilterAndSortByBe from '@/hooks/useFilterAndSortByBe';
import { defaultDonationFormValues } from './utils/DonationSchema';
import { Trash2 } from 'lucide-react';
import { getParamsWithAnimation, handleClearQueryParams } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { useToggleBooleanUrlParam } from '@/hooks/useToggleBooleanUrlParam';

const PAGE_TITLE = 'Donaciones';

const DonationPage: FC = () => {
  const navigate = useNavigate();

  const {
    pageIndex,
    pageSize,
    paginationState,
    dir,
    sort,
    showInactive,
    setPagination,
    setDir,
    setSort,
    setShowInactive,
    setFilters,
    filters,
  } = useTablePagination<DonationApi>();

  const { searchValue, form: searchBarForm, handleSubmit: handleSearchSubmit } = useSearchBar();

  const { data, isLoading, isFetching, refetch } = useDonations({
    page: pageIndex,
    size: pageSize,
    dir: dir,
    sort: sort,
    search: searchValue,
    costCenters: filters.costCenters,
    state: filters.state,
    showInactive: showInactive,
  });

  const { alertDialog, setAlertDialog, handleDismissAlertDialog } = useAlertDialog();

  const { mutate: deleteDonation, isPending: isDeleteDonationPending } = useMutation({
    mutationFn: deleteDonationMutationFn,
    onSuccess: () => {
      refetch();
      toast(
        <SuccessToastText>Donación eliminada exitosamente</SuccessToastText>,
        SONNER_SUCCESS_OPTIONS,
      );
      handleDismissAlertDialog();
    },
    onError: (error: AxiosError) => {
      Sentry.captureException(error);
      toast.error('No es posible eliminar la Donación debido a que tiene gastos asociados.', {
        description: 'Por favor, eliminá los gastos asociados e intentalo nuevamente.',
        ...SONNER_ERROR_OPTIONS,
      });
    },
  });

  const { mutate: toggleEnabled, isPending: isToggleEnabledPending } = useMutation({
    mutationFn: toggleEnabledMutationFn,
    onSuccess: (data: DonationApi) => {
      refetch();
      toast(
        <SuccessToastText>
          Donación <span className="text-blue-700">{data.name}</span> fue{' '}
          {data.enabled ? 'activada' : 'desactivada'} exitosamente!
        </SuccessToastText>,
        SONNER_SUCCESS_OPTIONS,
      );
      handleDismissAlertDialog();
    },
    onError: (error: AxiosError) => {
      Sentry.captureException(error);
      toast.error('Ha ocurrido un error durante la edición de la Donación.', {
        description: 'Por favor, intentalo nuevamente.',
        ...SONNER_ERROR_OPTIONS,
      });
    },
  });

  useFilterAndSortByBe<DonationApi>(
    refetch,
    setDir,
    setSort,
    setFilters,
    defaultDonationFormValues,
  );

  const toggleShowInactive = useToggleBooleanUrlParam({
    paramName: 'showInactive',
    onToggle: () => refetch(),
  });

  const handleEnableDonation = (donation: DonationApi) => {
    setAlertDialog({
      title: `¿Estás seguro que queres activar "${donation.name}"? `,
      description: `Esta acción volvera a activar la Donación "${donation.name}" y permitirá registrar cobros en ella.`,
      open: true,
      mainButtonVariant: 'primary',
      onCancel: handleDismissAlertDialog,
      onConfirm: () => {
        toggleEnabled(donation.id);
      },
    });
  };

  const handleDisableDonation = (donation: DonationApi) => {
    setAlertDialog({
      title: `¿Estás seguro que queres desactivar "${donation.name}"? `,
      description: `Esta acción desactivará la Donación "${donation.name}" y no permitirá registrar cobros en ella.`,
      open: true,
      onCancel: handleDismissAlertDialog,
      onConfirm: () => {
        toggleEnabled(donation.id);
      },
    });
  };

  const handleDeleteDonation = (donation: DonationApi) => {
    setAlertDialog({
      title: `¿Estás seguro que queres eliminar "${donation.name}"? `,
      description: `Esta acción no puede ser deshecha y se perderá toda la información asociada a esta Donación ("${donation.name}").`,
      open: true,
      onCancel: handleDismissAlertDialog,
      onConfirm: () => deleteDonation(donation.id),
    });
  };

  const handleOpenDonationDetail = (donation: DonationApi) => {
    navigate(`/${pathsBase.DONATIONS}/${donation.id}${getParamsWithAnimation(location.search)}`);
  };

  const handleShowInactiveDonations = () => {
    setShowInactive(!showInactive);
    toggleShowInactive(showInactive);
  };

  const columns = DonationTableColumns(
    handleOpenDonationDetail,
    handleDeleteDonation,
    handleEnableDonation,
    handleDisableDonation,
  );

  return (
    <>
      <SearchBar
        form={searchBarForm}
        searchTerm="donación"
        isLoading={isLoading || isFetching}
        submitHandler={handleSearchSubmit}
      />
      <TableWrapper
        columns={columns}
        data={data ? data[0] : data}
        isLoading={isLoading}
        isFetching={isFetching}
        rowsLabel={PAGE_TITLE}
        pageCount={data ? data[0].totalPages : DEFAULT_PAGINATION_PAGE_COUNT}
        pagination={paginationState}
        onPaginationChange={setPagination}
        emptyCta={
          location.search
            ? {
                label: 'Limpiar Filtros',
                onClick: () => handleClearQueryParams(navigate),
              }
            : undefined
        }
        emptyLogo={<Trash2 className="mr-2 h-4 w-4" />}
        leftComponent={
          <Button
            variant="outlinePrimary"
            size="sm"
            onClick={handleShowInactiveDonations}
            disabled={isFetching || isLoading}
          >
            {showInactive ? 'Ocultar Donaciones Inactivas' : 'Ver Donaciones Inactivas'}
          </Button>
        }
      />
      <AlertDialogWrapper
        {...alertDialog}
        isLoading={isDeleteDonationPending || isToggleEnabledPending}
        onDismiss={handleDismissAlertDialog}
      />
      <Outlet />
    </>
  );
};

export default DonationPage;
